// src/components/chatbot/ChatMessage.tsx
import React from 'react';
import { Bot } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { Message } from './types';
import ChatSuggestions from './ChatSuggestions';
import MessageFeedback from './MessageFeedback';

type ChatMessageProps = {
  message: Message;
  botAvatar: string;
  botName: string;
  onSuggestionClick: (suggestion: { id: string; text: string }) => void;
  isLastBotMessage?: boolean;
};

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  botAvatar,
  botName,
  onSuggestionClick,
  isLastBotMessage = false
}) => {
  const isBot = message.sender === 'bot';
  const isError = message.isError;
  const businessId = localStorage.getItem('businessId') || '';
  
  // Extract sourceId from message metadata if available
  // This would be the ID of the knowledge document or query-response pair
  // that was used to generate this response
  const sourceId = message.metadata?.sourceId || null;
  
  return (
    <div
      className={cn(
        "flex",
        isBot ? "justify-start" : "justify-end"
      )}
    >
      {isBot && (
        <Avatar className="h-8 w-8 mr-2 flex-shrink-0 mt-1 bg-primary/10">
          <AvatarImage src={botAvatar} alt={botName} />
          <AvatarFallback className="bg-primary/10">
            <Bot className="h-4 w-4 text-primary" />
          </AvatarFallback>
        </Avatar>
      )}
      
      <div className="flex flex-col">
        <div
          className={cn(
            "max-w-[250px] p-3 rounded-lg",
            isBot
              ? isError 
                ? "bg-destructive/10 text-destructive-foreground rounded-tl-none" 
                : "bg-muted rounded-tl-none"
              : "bg-primary text-primary-foreground rounded-tr-none"
          )}
        >
          <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
          <span className="text-xs opacity-70 mt-1 block text-right">
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        
        {isBot && message.suggestions && message.suggestions.length > 0 && (
          <div className="mt-2">
            <ChatSuggestions 
              suggestions={message.suggestions} 
              onSuggestionClick={onSuggestionClick} 
            />
          </div>
        )}

        {/* Add feedback component for bot messages */}
        {isBot && isLastBotMessage && businessId && !isError && (
          <div className="mt-1">
            <MessageFeedback 
              messageId={message.id} 
              businessId={businessId}
              sourceId={sourceId || undefined}
            />
          </div>
        )}
      </div>
      
      {!isBot && (
        <div className="w-8 flex-shrink-0" /> // Spacer for alignment
      )}
    </div>
  );
};

export default ChatMessage;